/**
 * Reads the same public, unauthenticated release manifest the desktop app's
 * in-app updater polls — see `apps/desktop/src-tauri/tauri.conf.json`'s
 * `plugins.updater.endpoints` and `apps/desktop/scripts/make-update-manifest.mjs`,
 * which is what writes `latest.json` at release time.
 *
 * No backend involved: the manifest and installers live in a public R2
 * bucket (the repository itself is private, so GitHub Releases can't be
 * linked from here), and this is a plain browser `fetch`.
 */

/** Mirrors the R2 bucket's `desktop/` prefix — kept in one place because the
 * updater endpoint, the NSIS/MSI download links, and the manifest URL below
 * all derive from it. Must match the base URL baked into `tauri.conf.json`
 * and passed to `make-update-manifest.mjs` in `.github/workflows/desktop.yml`. */
const R2_BASE_URL = "https://releases.bigbearbackup.com/desktop";

/** The manifest's on-the-wire shape — snake_case, since it's written by
 * `tauri-plugin-updater`'s own conventions, not by this codebase. */
interface RawManifest {
  version: string;
  notes: string;
  pub_date: string;
  platforms: {
    "windows-x86_64": { signature: string; url: string };
  };
  /** Absent on manifests published before checksums were added. */
  checksums?: { nsis_sha256: string; msi_sha256: string };
}

export interface LatestRelease {
  version: string;
  notes: string;
  pubDate: string;
  /** The NSIS installer — what the in-app updater itself installs, and the
   * recommended download for anyone getting the app for the first time. */
  nsisUrl: string;
  /** The MSI — never an updater target (see the release plan for why), but
   * published alongside it for Programs & Features / Group Policy /
   * Intune-style installs. Only `latest.json`'s NSIS entry is authoritative,
   * so this is derived from the same fixed naming convention
   * `make-update-manifest.mjs` stages installers under, not read from the
   * manifest itself. */
  msiUrl: string;
  /** SHA-256 of each installer, hex-encoded, for anyone verifying the
   * download independently of the link itself. Absent on releases published
   * before checksums were added to the manifest. */
  nsisSha256?: string;
  msiSha256?: string;
}

export async function fetchLatestRelease(
  signal?: AbortSignal,
): Promise<LatestRelease> {
  // `exactOptionalPropertyTypes` rejects passing `signal: undefined`, so an
  // absent signal means omitting the key rather than setting it.
  const response = await fetch(
    `${R2_BASE_URL}/latest.json`,
    signal ? { signal } : {},
  );
  if (!response.ok) {
    throw new Error(
      `Could not reach the release manifest (HTTP ${response.status}).`,
    );
  }
  const manifest = (await response.json()) as RawManifest;
  return {
    version: manifest.version,
    notes: manifest.notes,
    pubDate: manifest.pub_date,
    nsisUrl: manifest.platforms["windows-x86_64"].url,
    msiUrl: `${R2_BASE_URL}/Big-Bear-Backup_${manifest.version}_x64.msi`,
    ...(manifest.checksums
      ? {
          nsisSha256: manifest.checksums.nsis_sha256,
          msiSha256: manifest.checksums.msi_sha256,
        }
      : {}),
  };
}

/**
 * Same fixed naming convention `make-update-manifest.mjs` stages installers
 * under, so a page can link to a specific version's assets — including one
 * `releases/*.md` names that `latest.json` doesn't (yet, or any longer)
 * agree with — without waiting on a manifest fetch.
 */
export function downloadUrlsFor(version: string): {
  nsisUrl: string;
  msiUrl: string;
} {
  const base = `${R2_BASE_URL}/Big-Bear-Backup_${version}_x64`;
  return { nsisUrl: `${base}-setup.exe`, msiUrl: `${base}.msi` };
}
